import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { FileText, Plus, ExternalLink, ChevronDown, ChevronUp, List, Pencil, MessageSquare, AlertCircle } from 'lucide-react'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/empty-state'
import { supabase } from '@/lib/supabase/client'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { cn } from '@/lib/utils/cn'
import {
  useDeclineCommentsQuery,
  useFormCommentsQuery,
  useAddFormCommentMutation,
} from '../api/use-records-query'

interface FormGroup {
  id: string
  title: string
  icon: string | null
  display_order: number
  forms: Form[]
}

interface Form {
  id: string
  title: string
  icon: string | null
  instance_type: 'single' | 'repeatable'
  is_published: boolean
  required_for_final_submit: boolean
  minimum_instances: number | null
  submissions: Submission[]
}

interface Submission {
  id: string
  status: string
  instance_index: number
  created_at: string
  updated_at: string
}

async function fetchRecordForms(projectId: string, recordId: string) {
  const [groupsRes, formsRes, subsRes] = await Promise.all([
    supabase
      .from('form_groups')
      .select('id, title, icon, display_order')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .order('display_order'),
    supabase
      .from('forms')
      .select('id, title, icon, instance_type, is_published, required_for_final_submit, minimum_instances, group_id')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .order('display_order'),
    supabase
      .from('submissions')
      .select('id, status, instance_index, created_at, updated_at, form_id')
      .eq('record_id', recordId)
      .eq('is_deleted', false),
  ])

  if (groupsRes.error) throw groupsRes.error
  if (formsRes.error) throw formsRes.error

  const subs = subsRes.data ?? []

  return (groupsRes.data ?? []).map((g) => ({
    ...g,
    title: typeof g.title === 'object' ? (g.title as Record<string, string>).fa ?? '' : String(g.title),
    forms: (formsRes.data ?? [])
      .filter((f) => f.group_id === g.id && f.is_published)
      .map((f) => ({
        ...f,
        title: typeof f.title === 'object' ? (f.title as Record<string, string>).fa ?? '' : String(f.title),
        submissions: subs.filter((s) => s.form_id === f.id),
      })),
  })) as FormGroup[]
}

interface RecordFormsTabProps {
  projectId: string
  recordId: string
  /** When provided, clicking a form/instance calls this instead of navigating */
  onFormSelect?: (formId: string, submissionId?: string) => void
}

// ─── Form-level comments modal ───────────────────────────────────────────────

function FormCommentsDialog({
  recordId,
  submissionId,
  formTitle,
  onClose,
}: {
  recordId: string
  submissionId: string
  formTitle: string
  onClose: () => void
}) {
  const { role } = useProjectRole()
  const user = useAuthStore((s) => s.user)
  const [body, setBody] = useState('')
  const { data: comments = [] } = useFormCommentsQuery(recordId, submissionId)
  const addMutation = useAddFormCommentMutation(recordId)

  const handleAdd = async () => {
    if (!body.trim() || !user) return
    await addMutation.mutateAsync({ recordId, submissionId, body, userId: user.id, userRole: role ?? 'data_entry' })
    setBody('')
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent dir="rtl" className="max-w-md p-0 gap-0 flex flex-col max-h-[80vh]">
        <DialogHeader className="px-4 py-3 border-b border-border text-start sm:text-start">
          <DialogTitle className="text-sm flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            نظرات فرم «{formTitle}»
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {comments.length > 0 ? (
            <div className="space-y-1.5">
              {comments.map((c) => (
                <div key={c.id} className={cn('text-xs px-2 py-1.5 rounded', c.origin === 'decline' ? 'bg-red-500/10 text-red-300' : 'bg-muted text-muted-foreground')}>
                  <span className="font-medium text-foreground">{c.author?.full_name ?? '—'}: </span>
                  {c.body}
                </div>
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-xs text-muted-foreground">نظری ثبت نشده است</p>
          )}
        </div>

        {/* Composer pinned to the bottom of the modal */}
        <div className="px-4 py-3 bg-muted/20 border-t border-border flex gap-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="نظر روی این فرم..."
            className="text-xs min-h-[56px] resize-none"
            dir="rtl"
          />
          <Button size="sm" disabled={!body.trim() || addMutation.isPending} onClick={handleAdd} className="shrink-0 self-end">
            ارسال
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function RecordFormsTab({ projectId, recordId, onFormSelect }: RecordFormsTabProps) {
  const navigate = useNavigate()
  const { canEdit } = useProjectRole()

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['record-forms', projectId, recordId],
    queryFn: () => fetchRecordForms(projectId, recordId),
  })

  const { data: declineComments = [] } = useDeclineCommentsQuery(recordId)

  // submission_id → count of unresolved decline comments
  const declineCountBySubmission = declineComments.reduce<Record<string, number>>((acc, c) => {
    if (c.submission_id) acc[c.submission_id] = (acc[c.submission_id] ?? 0) + 1
    return acc
  }, {})

  // Form-level comments open in a modal (one at a time), not inline in the list
  const [commentDialog, setCommentDialog] = useState<{ submissionId: string; formTitle: string } | null>(null)

  // Expand all groups by default
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  // Set all groups expanded once data loads
  const [initializedGroups, setInitializedGroups] = useState(false)
  if (!initializedGroups && groups.length > 0) {
    setExpandedGroups(new Set(groups.map((g) => g.id)))
    setInitializedGroups(true)
  }

  const toggleGroup = (id: string) => {
    const next = new Set(expandedGroups)
    next.has(id) ? next.delete(id) : next.add(id)
    setExpandedGroups(next)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  const allForms = groups.flatMap((g) => g.forms)
  if (allForms.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="فرمی تعریف نشده"
        description="ابتدا از بخش فرم‌ساز، فرم‌های پروژه را تعریف و منتشر کنید"
        action={
          canEdit ? (
            <Button size="sm" onClick={() => navigate(`/projects/${projectId}/form-builder`)}>
              رفتن به فرم‌ساز
            </Button>
          ) : undefined
        }
      />
    )
  }

  return (
    <div className="space-y-3" dir="rtl">
      {groups.filter((g) => g.forms.length > 0).map((group) => {
        const isExpanded = expandedGroups.has(group.id)
        const groupSubmitted = group.forms.reduce(
          (sum, f) => sum + f.submissions.filter((s) => s.status === 'submitted').length,
          0
        )
        const groupTotal = group.forms.length

        return (
          <div key={group.id} className="bg-card border border-border rounded-md overflow-hidden">
            <button
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-muted/20 transition-colors"
              onClick={() => toggleGroup(group.id)}
            >
              <DynamicIcon name={group.icon} className="w-5 h-5" />
              <span className="flex-1 text-start font-medium text-sm text-foreground">{group.title}</span>
              <span className="text-xs text-muted-foreground me-1">
                {groupTotal} فرم
                {groupSubmitted > 0 && (
                  <span className="text-emerald-400 ms-1">· {groupSubmitted} ارسال شده</span>
                )}
              </span>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {isExpanded && (
              <div className="border-t border-border divide-y divide-border">
                {group.forms.map((form) => {
                  const submitted = form.submissions.filter((s) => s.status === 'submitted').length
                  const draft = form.submissions.filter((s) => s.status === 'draft').length
                  const total = form.submissions.length
                  const hasSubmission = total > 0
                  const minInstances = form.minimum_instances ?? (form.instance_type === 'repeatable' ? 1 : null)
                  const meetsMinimum =
                    form.instance_type === 'repeatable' && minInstances !== null
                      ? submitted >= minInstances
                      : true
                  const showSubRows = onFormSelect && form.instance_type === 'repeatable' && form.submissions.length > 0

                  const formDeclineCount = form.submissions.reduce(
                    (sum, s) => sum + (declineCountBySubmission[s.id] ?? 0), 0
                  )
                  const primarySubmissionId = form.submissions[0]?.id

                  return (
                    <div key={form.id}>
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className="relative shrink-0">
                          <DynamicIcon name={form.icon} className="w-4 h-4" />
                          {formDeclineCount > 0 && (
                            <span className="absolute -top-1.5 -end-1.5 w-3.5 h-3.5 bg-red-500 rounded-full flex items-center justify-center">
                              <AlertCircle className="w-2.5 h-2.5 text-white" />
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-foreground truncate">{form.title}</p>
                            {formDeclineCount > 0 && (
                              <span className="text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded shrink-0">
                                {formDeclineCount} نظر رد
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span
                              className={cn(
                                'text-xs',
                                !hasSubmission
                                  ? 'text-muted-foreground'
                                  : submitted > 0
                                  ? 'text-emerald-400'
                                  : 'text-yellow-400'
                              )}
                            >
                              {!hasSubmission
                                ? 'شروع نشده'
                                : form.instance_type === 'repeatable'
                                ? `${submitted} ارسال شده${draft > 0 ? ` · ${draft} پیش‌نویس` : ''}`
                                : submitted > 0
                                ? 'ارسال شده'
                                : 'پیش‌نویس'}
                            </span>
                            {form.required_for_final_submit ? (
                              <span className="text-xs text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">اجباری</span>
                            ) : (
                              <span className="text-xs text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">اختیاری</span>
                            )}
                            {form.instance_type === 'repeatable' && (
                              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">تکرارپذیر</span>
                            )}
                            {form.instance_type === 'repeatable' && minInstances !== null && !meetsMinimum && (
                              <span className="text-xs text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded">
                                حداقل {minInstances} مورد
                              </span>
                            )}
                          </div>

                          {/* I10 — inline status chips for each repeatable instance */}
                          {form.instance_type === 'repeatable' && form.submissions.length > 0 && (
                            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                              {form.submissions
                                .slice()
                                .sort((a, b) => a.instance_index - b.instance_index)
                                .map((s) => (
                                  <button
                                    key={s.id}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      if (onFormSelect) onFormSelect(form.id, s.id)
                                      else navigate(
                                        `/projects/${projectId}/records/${recordId}/forms/${form.id}?submissionId=${s.id}`,
                                      )
                                    }}
                                    className={cn(
                                      'text-[10px] px-1.5 py-0.5 rounded font-mono transition-colors',
                                      s.status === 'submitted'
                                        ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                                        : 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20',
                                    )}
                                    title={`نمونه ${s.instance_index.toLocaleString('fa-IR')} — ${s.status === 'submitted' ? 'ارسال شده' : 'پیش‌نویس'}`}
                                    aria-label={`نمونه ${s.instance_index.toLocaleString('fa-IR')} — ${s.status === 'submitted' ? 'ارسال شده' : 'پیش‌نویس'}`}
                                  >
                                    #{s.instance_index.toLocaleString('fa-IR')}
                                    {' '}
                                    {s.status === 'submitted' ? '✓' : '○'}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          {form.instance_type === 'repeatable' ? (
                            <>
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs gap-1"
                                  onClick={() =>
                                    onFormSelect
                                      ? onFormSelect(form.id, undefined)
                                      : navigate(`/projects/${projectId}/records/${recordId}/forms/${form.id}/new`)
                                  }
                                >
                                  افزودن
                                  <Plus className="w-3 h-3" />
                                </Button>
                              )}
                              {!onFormSelect && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs gap-1"
                                  onClick={() =>
                                    navigate(`/projects/${projectId}/records/${recordId}/forms/${form.id}/instances`)
                                  }
                                >
                                  مشاهده ({total})
                                  <List className="w-3 h-3" />
                                </Button>
                              )}
                            </>
                          ) : (
                            (hasSubmission || canEdit) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs gap-1"
                                onClick={() =>
                                  onFormSelect
                                    ? onFormSelect(form.id, form.submissions[0]?.id)
                                    : navigate(`/projects/${projectId}/records/${recordId}/forms/${form.id}`)
                                }
                              >
                                {canEdit ? (submitted > 0 ? 'مشاهده' : 'ویرایش') : 'مشاهده'}
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            )
                          )}
                          {primarySubmissionId && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className={cn(
                                'h-7 w-7 p-0 shrink-0',
                                commentDialog?.submissionId === primarySubmissionId && 'text-primary'
                              )}
                              onClick={() => setCommentDialog({ submissionId: primarySubmissionId, formTitle: form.title })}
                              title="نظر فرم"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Submission sub-rows for repeatable forms in workspace mode */}
                      {showSubRows && (
                        <div className="border-t border-border/50 bg-muted/20 divide-y divide-border/30">
                          {form.submissions
                            .slice()
                            .sort((a, b) => a.instance_index - b.instance_index)
                            .map((sub) => (
                              <button
                                key={sub.id}
                                className="w-full flex items-center gap-3 px-6 py-2 hover:bg-muted/40 transition-colors text-start"
                                onClick={() => onFormSelect!(form.id, sub.id)}
                              >
                                <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                <span className="flex-1 text-xs text-muted-foreground">
                                  نمونه {sub.instance_index}
                                </span>
                                <span
                                  className={cn(
                                    'text-[10px] px-1.5 py-0.5 rounded',
                                    sub.status === 'submitted'
                                      ? 'text-emerald-400 bg-emerald-500/10'
                                      : 'text-yellow-400 bg-yellow-500/10'
                                  )}
                                >
                                  {sub.status === 'submitted' ? 'ارسال شده' : 'پیش‌نویس'}
                                </span>
                                <Pencil className="w-3 h-3 text-muted-foreground shrink-0" />
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {commentDialog && (
        <FormCommentsDialog
          recordId={recordId}
          submissionId={commentDialog.submissionId}
          formTitle={commentDialog.formTitle}
          onClose={() => setCommentDialog(null)}
        />
      )}
    </div>
  )
}
